<div class="monitor-container"
     [ngStyle]="{'--border-color': borderColor, '--border-dim': dimLevel / 100}">

    <!-- Stop confirmation overlay -->
    @if (showStopConfirm) {
        <div class="confirm-overlay">
            <div class="confirm-card">
                <p class="confirm-title">Stop broadcasting?</p>
                <p class="confirm-sub">Parents watching will lose the feed.</p>
                <div class="confirm-actions">
                    <button class="confirm-cancel" (click)="showStopConfirm = false">Keep going</button>
                    <button class="confirm-stop" (click)="confirmStop()">Stop</button>
                </div>
            </div>
        </div>
    }

    <!-- Torch confirm overlay -->
    @if (showTorchConfirm) {
        <div class="confirm-overlay" (click)="showTorchConfirm = false">
            <div class="confirm-card" (click)="$event.stopPropagation()">
                <p class="confirm-title">Turn flashlight on?</p>
                <p class="confirm-sub">This may wake or discomfort the baby.</p>
                <div class="confirm-actions">
                    <button class="confirm-cancel" (click)="showTorchConfirm = false">Cancel</button>
                    <button class="confirm-stop" (click)="confirmTorchOn()">Turn on</button>
                </div>
            </div>
        </div>
    }

    <!-- Mic mute duration picker -->
    @if (showMuteOptions) {
        <div class="confirm-overlay" (click)="showMuteOptions = false">
            <div class="confirm-card" (click)="$event.stopPropagation()">
                <p class="confirm-title">Mute mic for</p>
                <div class="mute-duration-grid">
                    @for (mins of muteDurations; track mins) {
                        <button class="mute-duration-btn" (click)="confirmMicMute(mins)">{{ mins }}m</button>
                    }
                </div>
                <p class="confirm-sub" style="margin: 12px 0 0;">Tap outside to cancel</p>
            </div>
        </div>
    }

    <!-- Preview frame — inset by border on all four sides -->
    <div class="preview-frame">
        <video [srcObject]="stream" autoplay [muted]="true" playsinline></video>

        <!-- Viewer list (top-left, inside the preview) -->
        <div class="video-overlay">
            <div class="tech-info">
                <div class="viewer-list">
                    <div *ngFor="let v of viewers; let i = index"
                         class="viewer-item"
                         [class.parent-entry]="v.role === 'owner' || v.role === 'parent'">
                        <ng-container *ngIf="v.role === 'owner' || v.role === 'parent'; else guestRow">
                            <div class="parent-top">
                                <span>{{ ownerEmail || '...' }}</span>
                                <span class="debug-state"
                                      [class.state-connected]="v.state === 'connected' || v.state === 'completed'"
                                      [class.state-disconnected]="v.state !== 'connected' && v.state !== 'completed'">
                                    ({{ (v.state === 'connected' || v.state === 'completed') ? 'Online' : 'Offline' }})
                                </span>
                            </div>
                            <div class="parent-battery" [ngClass]="batteryColorClass(parentBatteryLevel, parentBatteryCharging)">
                                <span class="material-symbols-rounded">{{ batteryIcon(parentBatteryLevel, parentBatteryCharging) }}</span>
                                <span>{{ parentBatteryLevel !== null ? parentBatteryLevel + '%' : '—' }}</span>
                                @if (batteryTimeLabel(parentBatteryLevel, parentBatteryCharging, parentBatteryChargingTime, parentBatteryDischargingTime); as t) {
                                    <span>{{ t }}</span>
                                }
                            </div>
                        </ng-container>
                        <ng-template #guestRow>
                            <span>
                                <span class="dot"
                                      [class.connected]="v.state === 'connected' || v.state === 'completed'"
                                      [class.disconnected]="v.state !== 'connected' && v.state !== 'completed'"></span>
                                Guest {{ i + 1 }}
                            </span>
                            <span class="debug-state"
                                  [class.state-connected]="v.state === 'connected' || v.state === 'completed'"
                                  [class.state-disconnected]="v.state !== 'connected' && v.state !== 'completed'">
                                ({{ v.state }})
                            </span>
                        </ng-template>
                    </div>
                    <div *ngIf="viewers.length === 0">Waiting for connections...</div>
                </div>
            </div>
        </div>

        <!-- Volume slider panel (left side, toggled) -->
        @if (showVolSlider) {
            <div class="slider-panel left-panel">
                <span class="material-symbols-rounded panel-icon">volume_up</span>
                <input
                    class="vol-slider"
                    type="range"
                    min="0"
                    max="100"
                    [value]="volumeLevel"
                    (input)="onVolumeChange($event)">
                <span class="material-symbols-rounded panel-icon">volume_off</span>
            </div>
        }

        <!-- Dim slider panel (right side, toggled) -->
        @if (showDimSlider) {
            <div class="slider-panel right-panel">
                <span class="material-symbols-rounded panel-icon">light_mode</span>
                <input
                    class="dim-slider"
                    type="range"
                    min="0"
                    max="100"
                    [value]="dimLevel"
                    (input)="onDimChange($event)">
                <span class="material-symbols-rounded panel-icon">dark_mode</span>
            </div>
        }

        <!-- Bottom stack: local battery status row on top of the control bar -->
        <div class="bottom-stack">
            <div class="battery-row">
                <div class="battery-pill" [ngClass]="batteryColorClass(batteryLevel, batteryCharging)">
                    <span class="material-symbols-rounded battery-device-icon">videocam</span>
                    <span class="battery-label">This Device</span>
                    <span class="material-symbols-rounded">{{ batteryIcon(batteryLevel, batteryCharging) }}</span>
                    <span class="battery-pct">{{ batteryLevel !== null ? batteryLevel + '%' : '—' }}</span>
                    @if (batteryTimeLabel(batteryLevel, batteryCharging, batteryChargingTime, batteryDischargingTime); as t) {
                        <span class="battery-time">· {{ t }}</span>
                    }
                </div>
            </div>

            <!-- Bottom control bar — stays inside the preview, above the border -->
            <div class="control-bar">
            <button class="control-btn" [class.active]="showVolSlider" (click)="toggleVolSlider()">
                <span class="material-symbols-rounded">volume_up</span>
                <span class="btn-label">Vol</span>
            </button>
            <button class="control-btn" [class.active]="showDimSlider" (click)="toggleDimSlider()">
                <span class="material-symbols-rounded">brightness_6</span>
                <span class="btn-label">Dim</span>
            </button>
            <button class="control-btn" (click)="toggleCamera()">
                <span class="material-symbols-rounded">switch_camera</span>
                <span class="btn-label">Flip</span>
            </button>
            <button class="control-btn" [class.active]="isMicMuted" (click)="toggleMicMute()">
                <span class="material-symbols-rounded">{{ isMicMuted ? 'mic_off' : 'mic' }}</span>
                <span class="btn-label">{{ isMicMuted ? micCountdownDisplay : 'Mic' }}</span>
            </button>
            <button class="control-btn" [class.active]="videoMuted" (click)="toggleVideoMute()">
                <span class="material-symbols-rounded">{{ videoMuted ? 'videocam_off' : 'videocam' }}</span>
                <span class="btn-label">{{ videoMuted ? 'Off' : 'Cam' }}</span>
            </button>
            <button class="control-btn" [class.active]="torchOn" (click)="toggleTorch()">
                <span class="material-symbols-rounded">{{ torchOn ? 'flashlight_on' : 'flashlight_off' }}</span>
                <span class="btn-label">Light</span>
            </button>
            <button class="control-btn" (click)="toggleFullscreen()">
                <span class="material-symbols-rounded">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</span>
                <span class="btn-label">{{ isFullscreen ? 'Exit' : 'Full' }}</span>
            </button>
            <button class="control-btn stop-btn" (click)="showStopConfirm = true">
                <span class="material-symbols-rounded">stop_circle</span>
                <span class="btn-label">Stop</span>
            </button>
            </div>
        </div>
    </div>

</div>
